import api from './api';
import { revalidate } from './mutations';

export interface ConditionalFormattingRule {
  id: number;
  userId: number;
  moduleName: string;
  columnKey: string;
  name: string;
  description?: string;
  scope: 'column' | 'row';
  condition: {
    type: 'column' | 'expression';
    columnKey?: string;
    expression?: string;
    operator: string;
    value: any;
    value2?: any;
  };
  style: {
    backgroundColor?: string;
    textColor?: string;
    fontWeight?: string;
    borderColor?: string;
  };
  isActive: boolean;
  priority: number;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateConditionalFormattingRuleDto {
  moduleName: string;
  columnKey: string;
  name: string;
  description?: string;
  scope?: 'column' | 'row';
  condition: {
    type: 'column' | 'expression';
    columnKey?: string;
    expression?: string;
    operator: string;
    value: any;
    value2?: any;
  };
  style: {
    backgroundColor?: string;
    textColor?: string;
    fontWeight?: string;
    borderColor?: string;
  };
  isActive?: boolean;
  priority?: number;
  isDefault?: boolean;
}

export interface UpdateConditionalFormattingRuleDto extends Partial<CreateConditionalFormattingRuleDto> {}

/**
 * Get all conditional formatting rules for a module
 */
export const getConditionalFormattingRules = async (moduleName?: string): Promise<ConditionalFormattingRule[]> => {
  const params = moduleName ? `?moduleName=${encodeURIComponent(moduleName)}` : '';
  const response = await api.get(`/conditional-formatting${params}`);
  return response.data;
};

/**
 * Get a specific conditional formatting rule by ID
 */
export const getConditionalFormattingRule = async (id: number): Promise<ConditionalFormattingRule> => {
  const response = await api.get(`/conditional-formatting/${id}`);
  return response.data;
};

/**
 * Get the default conditional formatting rule for a module
 */
export const getDefaultConditionalFormattingRule = async (moduleName: string): Promise<ConditionalFormattingRule | null> => {
  try {
    const response = await api.get(`/conditional-formatting/default/${encodeURIComponent(moduleName)}`);
    return response.data;
  } catch (error: any) {
    // If 404, return null (no default rule exists)
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

/**
 * Create a new conditional formatting rule
 */
export const createConditionalFormattingRule = async (data: CreateConditionalFormattingRuleDto): Promise<ConditionalFormattingRule> => {
  const response = await api.post('/conditional-formatting', data);
  revalidate(['conditional-formatting', `conditional-formatting-${data.moduleName}`]);
  return response.data;
};

/**
 * Update a conditional formatting rule
 */
export const updateConditionalFormattingRule = async (id: number, data: UpdateConditionalFormattingRuleDto, moduleName?: string): Promise<ConditionalFormattingRule> => {
  const response = await api.patch(`/conditional-formatting/${id}`, data);
  // Use moduleName from response if not provided
  const ruleModuleName = moduleName || response.data?.moduleName || 'all';
  revalidate(['conditional-formatting', `conditional-formatting-${ruleModuleName}`]);
  return response.data;
};

/**
 * Set a conditional formatting rule as default
 */
export const setDefaultConditionalFormattingRule = async (id: number): Promise<ConditionalFormattingRule> => {
  const response = await api.patch(`/conditional-formatting/${id}/set-default`);
  revalidate(['conditional-formatting']);
  return response.data;
};

/**
 * Delete a conditional formatting rule
 */
export const deleteConditionalFormattingRule = async (id: number, moduleName?: string): Promise<void> => {
  await api.delete(`/conditional-formatting/${id}`);
  revalidate(['conditional-formatting', moduleName ? `conditional-formatting-${moduleName}` : 'conditional-formatting']);
};

